import React, { useState, useEffect } from "react";
import Flatpickr from "react-flatpickr";
import MaskedInput from 'react-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

import { useRentalInvoiceEditContext } from "./RentalInvoiceEditContainer";

const baseToggleStyle = {
  border: "1px solid var(--sl-border-primary, #d5d7da)",
  padding: "8px 12px",
  cursor: "pointer",
  fontSize: "14px",
};

const leftToggleStyle = {
  ...baseToggleStyle,
  borderRadius: "8px 0px 0px 8px",
};

const rightToggleStyle = {
  ...baseToggleStyle,
  borderRadius: "0px 8px 8px 0px",
};

const inputWrapperStyle = {
  border: "1px solid var(--sl-border-secondary, #ececec)",
  borderRadius: "8px",
  width: "100%",
};

const toggleActiveStyle = {
  backgroundColor: "var(--sl-bg-tertiary, #f0f0f0)",
  color: "var(--sl-text-primary, #181d27)",
};

const toggleInactiveStyle = {
  backgroundColor: "var(--sl-bg-primary, #fff)",
  color: "var(--sl-text-tertiary, #535862)",
};

const RentalInvoiceDepositRow = ({ deposit }) => {
  const {
    updateNestedAttributes,
    deleteNested,
  } = useRentalInvoiceEditContext();

  const [depositValue, setDepositValue] = useState(deposit.value);
  const [depositRelativeDueDate, setDepositRelativeDueDate] = useState(deposit.relative_due_date);
  const [depositSpecificDueDate, setDepositSpecificDueDate] = useState(deposit.specific_due_date ? new Date(deposit.specific_due_date) : null);

  const updateDepositState = (attributes) => {
    updateNestedAttributes("deposits_attributes", {
      id: deposit.id,
      ...attributes,
    });
  };

  useEffect(() => {
    if (deposit.value !== depositValue) {
      updateDepositState({ value: depositValue });
    }
  }, [depositValue]);

  useEffect(() => {
    if (deposit.relative_due_date !== depositRelativeDueDate) {
      updateDepositState({ relative_due_date: depositRelativeDueDate });
    }
  }, [depositRelativeDueDate]);

  useEffect(() => {
    if (deposit.specific_due_date !== depositSpecificDueDate) {
      updateDepositState({ specific_due_date: depositSpecificDueDate });
    }
  }, [depositSpecificDueDate]);

  const deleteDeposit = (id) => {
    deleteNested("deposits_attributes", id);
  };

  return (
    <div className="d-flex flex-column">
      <div className="d-flex flex-wrap" style={{ gap: "20px" }}>
        <div className="flex-grow-1" style={{ minWidth: "200px" }}>
          <div className="d-flex flex-column">
            <label className="setlist-text-sm figma-text secondary mb-2">Type</label>
            <div className="btn-group btn-group-sm" role="group">
              <button
                type="button"
                style={{
                  ...leftToggleStyle,
                  ...(deposit.deposit_type === "percent" ? toggleActiveStyle : toggleInactiveStyle),
                }}
                onClick={() => updateDepositState({ deposit_type: "percent" })}
              >
                Percent
              </button>
              <button
                type="button"
                style={{
                  ...rightToggleStyle,
                  ...(deposit.deposit_type === "flat" ? toggleActiveStyle : toggleInactiveStyle),
                }}
                onClick={() => updateDepositState({ deposit_type: "flat" })}
              >
                Flat
              </button>
            </div>
          </div>
        </div>
        <div className="flex-grow-1" style={{ minWidth: "200px" }}>
          <div className="d-flex flex-column">
            <label className="setlist-text-sm figma-text secondary mb-2">
              {deposit.deposit_type === "percent" ? "Percent" : "Amount"}
            </label>
            {deposit.deposit_type === "percent" ? (
              <div className="input-group inline-white" style={inputWrapperStyle}>
                <MaskedInput
                  id="deposit-percent"
                  className="form-control"
                  placeholder={"0"}
                  mask={createNumberMask({
                    prefix: "",
                    allowDecimal: true,
                    decimalLimit: 2,
                    allowNegative: false,
                    includeThousandsSeparator: false
                  })}
                  value={depositValue}
                  onFocus={e => e.target.select()}
                  onChange={e => { setDepositValue(e.target.value) }}
                />
                <div className="input-group-append">
                  <span className="input-group-text px-2">
                    <i className="far fa-percentage text-muted"></i>
                  </span>
                </div>
              </div>
            ) : (
              <div className="input-group inline-white" style={inputWrapperStyle}>
                <div className="input-group-prepend">
                  <span className="input-group-text px-2">
                    <i className="far fa-dollar-sign text-muted"></i>
                  </span>
                </div>
                <MaskedInput
                  id="deposit-flat"
                  className="form-control"
                  placeholder={"0"}
                  mask={createNumberMask({
                    prefix: "",
                    allowDecimal: true,
                    decimalLimit: 2,
                    allowNegative: false,
                    includeThousandsSeparator: false
                  })}
                  value={depositValue}
                  onFocus={e => e.target.select()}
                  onChange={e => { setDepositValue(e.target.value) }}
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex-grow-1" style={{ minWidth: "200px" }}>
          <div className="d-flex flex-column">
            <label className="setlist-text-sm figma-text secondary mb-2">Due Date</label>
            <div className="btn-group btn-group-sm" role="group">
              <button
                type="button"
                style={{
                  ...leftToggleStyle,
                  ...(deposit.due_date_type === "relative" ? toggleActiveStyle : toggleInactiveStyle),
                }}
                onClick={() => updateDepositState({ due_date_type: "relative" })}
              >
                Relative
              </button>
              <button
                type="button"
                style={{
                  ...rightToggleStyle,
                  ...(deposit.due_date_type === "specific" ? toggleActiveStyle : toggleInactiveStyle),
                }}
                onClick={() => {
                  const now = new Date();
                  updateDepositState({
                    due_date_type: "specific",
                    specific_due_date: now
                  });
                }}
              >
                Specific
              </button>
            </div>
          </div>
        </div>
        <div className="flex-grow-1" style={{ minWidth: "200px" }}>
          <div className="d-flex flex-column">
            <label className="setlist-text-sm figma-text secondary mb-2">
              {deposit.due_date_type === "relative" ? "Days Before Event" : "Date"}
            </label>
            {deposit.due_date_type === "relative" ? (
              <div className="input-group inline-white" style={inputWrapperStyle}>
                <MaskedInput
                  id="deposit-relative"
                  className="form-control"
                  placeholder={"0"}
                  mask={createNumberMask({
                    prefix: "",
                    decimalLimit: 0,
                    allowNegative: false,
                    includeThousandsSeparator: false
                  })}
                  value={depositRelativeDueDate}
                  onFocus={e => e.target.select()}
                  onChange={e => { setDepositRelativeDueDate(e.target.value) }}
                />
              </div>
            ) : (
              <Flatpickr
                className={"form-control form-control-flatpickr"}
                style={{ border: "1px solid var(--sl-border-secondary, #ececec)", width: "100%" }}
                disabled={false}
                options={{
                  enableTime: false,
                  dateFormat: "n/j/y",
                  time_24hr: true,
                  utc: true
                }}
                placeholder={"Select date"}
                value={depositSpecificDueDate}
                onClose={dates => setDepositSpecificDueDate(dates[0])}
              />
            )}
          </div>
        </div>
        <div className="d-flex align-items-end" style={{ paddingBottom: "6px" }}>
          <a onClick={() => deleteDeposit(deposit.id)} className="setlist-text-sm figma-text quinary" style={{ cursor: "pointer" }}>
            <i className="far fa-trash" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default RentalInvoiceDepositRow;
